import {
  U21Alert,
  U21Button,
  U21Chip,
  U21Divider,
  U21Modal,
  U21Spacer,
  U21Typography,
} from 'app/shared/u21-ui/components';

import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { evaluateAndEnrich } from 'app/modules/rules/helpers';
import { Operand } from 'app/modules/detectionModels/components/ConditionBreakdown/Operand';
import { AlertHitResponse } from 'app/modules/alerts/types/responses';
import { formatDatetime } from 'app/shared/utils/date';
import { CompositeQueryTreeNode } from 'app/modules/rulesAdvanced/types';
import { RuleConditionTooltips } from 'app/modules/detectionModels/constants';

export interface ConditionBreakdownProps {
  ruleCondition: CompositeQueryTreeNode | null;
  open: boolean;
  close: () => void;
  alertId: number;
  ruleTitle: string | undefined;
  alertHits: AlertHitResponse[];
  alertHitIndex: number;
  totalAlertHitIndex: number;
  onHeaderAction: (n: number) => void;
  loading?: boolean;
}

export const ConditionBreakdown = ({
  ruleCondition,
  open,
  close,
  alertId,
  ruleTitle,
  alertHits,
  alertHitIndex,
  totalAlertHitIndex,
  onHeaderAction,
  loading = false,
}: ConditionBreakdownProps) => {
  if (alertHitIndex < 0) {
    return null;
  }

  const snapshot = alertHits[alertHitIndex]?.variable_snapshots || {};
  const hasSnapshot = alertHits && Object.keys(snapshot).length > 0;
  const createdAt = alertHits[alertHitIndex]?.created_at || '';
  const enrichedRuleCondition = ruleCondition
    ? evaluateAndEnrich(
        ruleCondition,
        snapshot,
        alertHits[alertHitIndex]?.window_end,
      )
    : undefined;

  const renderAlert = (message: string) => (
    <U21Alert severity="warning">
      <U21Typography variant="body2" component="div">
        {message}
      </U21Typography>
    </U21Alert>
  );

  const getBodyContent = () => {
    // No snapshot
    if (!hasSnapshot) {
      return renderAlert(RuleConditionTooltips.RULE_CONDITIONS_NOT_AVAILABLE);
    }

    // Error enriching condition
    if (!enrichedRuleCondition) {
      return renderAlert('Error evaluating rule condition.');
    }

    return enrichedRuleCondition.operands.map((operand, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <U21Spacer key={index}>
        <Operand
          op={operand}
          group={enrichedRuleCondition.operator}
          isFirst={index === 0}
        />
        <div />
      </U21Spacer>
    ));
  };

  return (
    <U21Modal
      open={open}
      size="large"
      onClose={() => close()}
      title="Rule Conditions"
      loading={loading}
      headerAction={
        <U21Spacer horizontal>
          <div>
            Alert Updates {alertHitIndex + 1} of {totalAlertHitIndex}
          </div>
          <U21Button
            onClick={() => onHeaderAction(alertHitIndex - 1)}
            disabled={alertHitIndex === 0}
          >
            <ChevronLeft />
          </U21Button>
          <U21Button
            onClick={() => onHeaderAction(alertHitIndex + 1)}
            disabled={alertHitIndex === totalAlertHitIndex - 1}
          >
            <ChevronRight />
          </U21Button>
          <U21Divider flexItem />
        </U21Spacer>
      }
    >
      <U21Spacer spacing={2}>
        <div>
          <U21Typography variant="body1" color="black" component="div">
            <U21Chip>Alert #{alertId}</U21Chip> was{' '}
            {alertHitIndex === totalAlertHitIndex - 1 ? 'flagged' : 'updated'}{' '}
            by <U21Chip>{ruleTitle}</U21Chip> on {formatDatetime(createdAt)}.
          </U21Typography>
        </div>

        <div>{getBodyContent()}</div>
      </U21Spacer>
    </U21Modal>
  );
};
